/**
 * VÉRAULT Portal — 3-Tier Progressive Client Profile
 *
 * A luxury, editorial reading-room approach to client intake.  Three
 * collapsible sections grow with the relationship:
 *
 *   I.   The Essentials        — name, contact, preferred channel
 *   II.  Travel Details        — passport, loyalty, dietary, budget
 *   III. The Inner Circle      — celebrations, gifting, longform notes
 *
 * Tier I is always expanded.  Tier II/III collapse-by-default with a
 * gentle completion indicator and a single Edit affordance per section.
 * Save diffs only changed keys (no clobbering legacy data).
 */
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  Pencil,
  CheckCircle2,
  X as XIcon,
  Loader2,
  Plus,
  Trash2,
} from "lucide-react";

import { Surface, Heading, KeyValue, PortalButton, api } from "../../../index";

// ---------------------------------------------------------------------
// Field definitions per tier
// ---------------------------------------------------------------------
const TIER_I_FIELDS = [
  { key: "first_name", label: "First name", type: "text", required: true },
  { key: "last_name", label: "Last name", type: "text", required: true },
  { key: "email", label: "Email", type: "email" },
  { key: "phone", label: "Phone", type: "tel" },
  { key: "address", label: "Address", type: "text", placeholder: "Street, city, country" },
  { key: "date_of_birth", label: "Date of birth", type: "date" },
];

const TIER_II_FIELDS = [
  { key: "nationality", label: "Nationality", type: "text" },
  { key: "passport_number", label: "Passport #", type: "text" },
  { key: "passport_issuing_country", label: "Issuing country", type: "text" },
  { key: "passport_expiration_date", label: "Passport expires", type: "date" },
  { key: "home_airport", label: "Home airport", type: "text", placeholder: "JFK · LAX · ORD" },
  { key: "seat_preference", label: "Seat preference", type: "text", placeholder: "Aisle · Window" },
  { key: "known_traveler_number", label: "Known traveler #", type: "text" },
  { key: "redress_number", label: "Redress #", type: "text" },
  { key: "dietary_needs", label: "Dietary needs", type: "text" },
  { key: "allergies", label: "Allergies", type: "text" },
  { key: "mobility_needs", label: "Mobility needs", type: "text" },
  {
    key: "preferred_communication",
    label: "Preferred channel",
    type: "select",
    options: ["", "email", "phone", "text", "portal"],
  },
  {
    key: "budget_range",
    label: "Budget range",
    type: "select",
    options: ["", "Under $10k", "$10k–$25k", "$25k–$50k", "$50k–$100k", "$100k+", "Unspecified"],
  },
  {
    key: "travel_pace",
    label: "Travel pace",
    type: "select",
    options: ["", "slow", "balanced", "active", "intense"],
  },
  {
    key: "travel_style",
    label: "Travel style",
    type: "text",
    placeholder: "Culinary · Wellness · Cultural",
  },
];

const TIER_III_FIELDS = [
  {
    key: "vip_status",
    label: "VIP status",
    type: "select",
    options: ["", "standard", "preferred", "vip", "legacy"],
  },
  { key: "celebration_dates", label: "Celebrations", type: "text", placeholder: "Anniversary · Birthdays" },
  { key: "concierge_preferences", label: "Concierge preferences", type: "textarea", rows: 3 },
  { key: "relationship_notes", label: "Relationship notes (internal)", type: "textarea", rows: 4 },
  { key: "favorite_destinations", label: "Favorite destinations", type: "chips" },
  { key: "preferred_hotels", label: "Preferred hotels", type: "chips" },
  { key: "preferred_airlines", label: "Preferred airlines", type: "chips" },
  { key: "loyalty_programs", label: "Loyalty programs", type: "loyalty" },
  { key: "emergency_contact_name", label: "Emergency contact name", type: "text" },
  { key: "emergency_contact_phone", label: "Emergency contact phone", type: "tel" },
  { key: "emergency_contact_relationship", label: "Emergency relationship", type: "text" },
];

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------
function isFilled(value) {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return Boolean(value);
}

function countFilled(client, fields) {
  let n = 0;
  for (const f of fields) if (isFilled(client?.[f.key])) n += 1;
  return n;
}

function displayValue(client, field) {
  const v = client?.[field.key];
  if (!isFilled(v)) return null;
  if (field.type === "chips") return Array.isArray(v) ? v.join(" · ") : v;
  if (field.type === "loyalty") {
    if (!Array.isArray(v)) return String(v);
    return v.map((row) => (row.program ? `${row.program}${row.number ? " · " + row.number : ""}` : null)).filter(Boolean).join(" · ");
  }
  if (field.type === "date" && typeof v === "string") {
    // Render as YYYY-MM-DD verbatim for the editorial KeyValue
    return v;
  }
  return String(v);
}

// ---------------------------------------------------------------------
// Inline field renderers
// ---------------------------------------------------------------------
const INPUT_CLS =
  "w-full px-3 py-2 text-[14px] bg-[hsl(var(--background))] border border-transparent rounded-md outline-none focus:border-[rgba(0,0,0,0.12)] transition-colors";
const LABEL_CLS =
  "font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] mb-1.5 block";

function ChipsInput({ value, onChange, placeholder, testid }) {
  const [draft, setDraft] = React.useState("");
  const list = Array.isArray(value) ? value : [];

  const addChip = () => {
    const t = draft.trim();
    if (!t) return;
    if (list.includes(t)) {
      setDraft("");
      return;
    }
    onChange([...list, t]);
    setDraft("");
  };
  const removeChip = (idx) => {
    const next = [...list];
    next.splice(idx, 1);
    onChange(next);
  };
  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {list.map((c, i) => (
          <span
            key={`${c}-${i}`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[hsl(var(--background-paper))] border border-[rgba(0,0,0,0.05)] text-[12px] font-body"
          >
            {c}
            <button
              type="button"
              onClick={() => removeChip(i)}
              className="text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-terracotta))]"
              aria-label={`Remove ${c}`}
            >
              <XIcon className="size-3" strokeWidth={1.5} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input
          className={INPUT_CLS}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder || "Type & press Enter"}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addChip();
            }
          }}
        />
        <button
          type="button"
          onClick={addChip}
          className="shrink-0 size-9 rounded-md inline-flex items-center justify-center text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] hover:bg-[hsl(var(--background-paper))]"
          aria-label="Add"
        >
          <Plus className="size-4" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}

function LoyaltyInput({ value, onChange, testid }) {
  const list = Array.isArray(value) ? value : [];
  const update = (idx, patch) => {
    const next = list.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  };
  const remove = (idx) => {
    const next = [...list];
    next.splice(idx, 1);
    onChange(next);
  };
  const add = () => onChange([...list, { program: "", number: "" }]);
  return (
    <div className="space-y-2" data-testid={testid}>
      {list.map((row, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
          <input
            className={INPUT_CLS}
            placeholder="Program (Hyatt, AAdvantage…)"
            value={row.program || ""}
            onChange={(e) => update(i, { program: e.target.value })}
          />
          <input
            className={INPUT_CLS}
            placeholder="Number"
            value={row.number || ""}
            onChange={(e) => update(i, { number: e.target.value })}
          />
          <button
            type="button"
            onClick={() => remove(i)}
            className="size-9 rounded-md inline-flex items-center justify-center text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-terracotta))] hover:bg-[hsl(var(--background-paper))]"
            aria-label="Remove program"
          >
            <Trash2 className="size-3.5" strokeWidth={1.5} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="inline-flex items-center gap-1.5 text-[12px] font-body text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] transition-colors"
      >
        <Plus className="size-3.5" strokeWidth={1.5} /> Add program
      </button>
    </div>
  );
}

function FieldEditor({ field, value, onChange }) {
  const testid = `profile-field-${field.key}`;
  if (field.type === "textarea") {
    return (
      <textarea
        className={INPUT_CLS}
        rows={field.rows || 3}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        data-testid={testid}
      />
    );
  }
  if (field.type === "select") {
    return (
      <select
        className={INPUT_CLS}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      >
        {field.options.map((o) => (
          <option key={o} value={o}>
            {o ? o.charAt(0).toUpperCase() + o.slice(1) : "—"}
          </option>
        ))}
      </select>
    );
  }
  if (field.type === "chips") {
    return <ChipsInput value={value} onChange={onChange} placeholder={field.placeholder} testid={testid} />;
  }
  if (field.type === "loyalty") {
    return <LoyaltyInput value={value} onChange={onChange} testid={testid} />;
  }
  return (
    <input
      type={field.type || "text"}
      className={INPUT_CLS}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.placeholder}
      data-testid={testid}
    />
  );
}

// ---------------------------------------------------------------------
// One tier — collapsible editorial section with view/edit modes
// ---------------------------------------------------------------------
function Tier({
  numeral,
  title,
  lede,
  fields,
  client,
  clientId,
  onRefresh,
  defaultOpen = false,
  testid,
}) {
  const [open, setOpen] = React.useState(defaultOpen);
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const filled = countFilled(client, fields);
  const total = fields.length;
  const pct = Math.round((filled / total) * 100);

  React.useEffect(() => {
    if (editing) {
      const seed = {};
      for (const f of fields) {
        const v = client?.[f.key];
        seed[f.key] = v === undefined ? (f.type === "chips" || f.type === "loyalty" ? [] : "") : v;
      }
      setDraft(seed);
      setErr(null);
    }
  }, [editing, client, fields]);

  const startEdit = () => {
    setOpen(true);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setErr(null);
  };

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      // Diff: only send changed keys
      const patch = {};
      for (const f of fields) {
        const cur = client?.[f.key];
        const next = draft?.[f.key];
        const normNext =
          f.type === "chips" || f.type === "loyalty"
            ? Array.isArray(next) ? next : []
            : typeof next === "string" ? next : next ?? "";
        const normCur =
          f.type === "chips" || f.type === "loyalty"
            ? Array.isArray(cur) ? cur : []
            : typeof cur === "string" ? cur : cur ?? "";
        const sameArr =
          Array.isArray(normNext) &&
          Array.isArray(normCur) &&
          JSON.stringify(normNext) === JSON.stringify(normCur);
        const same = sameArr || normNext === normCur;
        if (!same) {
          // Convert empty strings to null so server can store blanks, but keep arrays as []
          if (f.type === "chips" || f.type === "loyalty") {
            patch[f.key] = normNext;
          } else {
            patch[f.key] = normNext === "" ? "" : normNext;
          }
        }
      }
      if (Object.keys(patch).length === 0) {
        setEditing(false);
        setSaving(false);
        return;
      }
      // Auto-derive full_name from first_name + last_name whenever either changed.
      // This keeps the legacy `full_name` field (still required by Pydantic) in sync
      // even though the new Tier I UI no longer surfaces it.
      if ("first_name" in patch || "last_name" in patch) {
        const first = patch.first_name ?? client?.first_name ?? "";
        const last = patch.last_name ?? client?.last_name ?? "";
        const composed = `${String(first).trim()} ${String(last).trim()}`.trim();
        if (composed && composed !== (client?.full_name || "")) {
          patch.full_name = composed;
        }
      }
      await api.patch(`/clients/${clientId}`, patch);
      setEditing(false);
      onRefresh?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const barTone =
    pct >= 80
      ? "bg-[hsl(var(--accent-sage))]"
      : pct >= 30
      ? "bg-[hsl(var(--accent-gold))]"
      : "bg-[hsl(var(--foreground-muted))]/40";

  return (
    <Surface variant="paper" padded className="!p-0 overflow-hidden" data-testid={testid}>
      {/* Header */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-5 px-6 md:px-7 py-5 text-left group"
        data-testid={`${testid}-header`}
      >
        <span className="font-mono text-[11px] tracking-[0.32em] text-[hsl(var(--accent-gold))]">
          {numeral}
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-serif text-[22px] md:text-[24px] leading-tight text-[hsl(var(--foreground))]">
            {title}
          </h3>
          <p className="mt-1 font-accent italic text-[13px] text-[hsl(var(--foreground-secondary))]">
            {lede}
          </p>
        </div>
        <div className="hidden sm:flex flex-col items-end gap-1.5 shrink-0">
          <span
            className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]"
            data-testid={`${testid}-progress`}
          >
            {filled} of {total} on file
          </span>
          <span className="block w-28 h-[2px] bg-[rgba(0,0,0,0.06)] rounded-full overflow-hidden">
            <span
              className={`block h-full ${barTone} transition-all duration-500`}
              style={{ width: `${pct}%` }}
            />
          </span>
        </div>
        <ChevronDown
          className={`size-4 shrink-0 text-[hsl(var(--foreground-muted))] transition-transform duration-300 ${
            open ? "rotate-180" : ""
          }`}
          strokeWidth={1.5}
        />
      </button>

      {/* Body */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="border-t border-[rgba(0,0,0,0.05)] px-6 md:px-7 py-6">
              {!editing ? (
                <>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
                    {fields.map((f) => (
                      <KeyValue
                        key={f.key}
                        label={f.label}
                        value={displayValue(client, f)}
                        testid={`profile-kv-${f.key}`}
                      />
                    ))}
                  </div>
                  <div className="mt-7 flex items-center justify-end">
                    <PortalButton
                      variant="ghost"
                      size="sm"
                      onClick={startEdit}
                      icon={<Pencil className="size-3.5" strokeWidth={1.5} />}
                      data-testid={`${testid}-edit-btn`}
                    >
                      Edit details
                    </PortalButton>
                  </div>
                </>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    save();
                  }}
                >
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
                    {fields.map((f) => (
                      <div key={f.key} className={f.type === "textarea" || f.type === "chips" || f.type === "loyalty" ? "sm:col-span-2 lg:col-span-3" : ""}>
                        <span className={LABEL_CLS}>
                          {f.label}
                          {f.required ? " *" : ""}
                        </span>
                        <FieldEditor
                          field={f}
                          value={draft[f.key]}
                          onChange={(v) =>
                            setDraft((d) => ({ ...d, [f.key]: v }))
                          }
                        />
                      </div>
                    ))}
                  </div>
                  {err && (
                    <p
                      className="mt-5 font-body text-[13px] text-[hsl(var(--accent-terracotta))]"
                      data-testid={`${testid}-error`}
                    >
                      {err}
                    </p>
                  )}
                  <div className="mt-7 flex items-center justify-end gap-2 pt-5 border-t border-[rgba(0,0,0,0.05)]">
                    <PortalButton
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={cancel}
                      disabled={saving}
                    >
                      Cancel
                    </PortalButton>
                    <PortalButton
                      type="submit"
                      variant="primary"
                      size="sm"
                      disabled={saving}
                      icon={
                        saving ? (
                          <Loader2 className="size-3.5 animate-spin" />
                        ) : (
                          <CheckCircle2 className="size-3.5" strokeWidth={1.5} />
                        )
                      }
                      data-testid={`${testid}-save-btn`}
                    >
                      {saving ? "Saving" : "Save"}
                    </PortalButton>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Surface>
  );
}

// ---------------------------------------------------------------------
// Public — three tiers stacked
// ---------------------------------------------------------------------
export function ProfileTiers({ client, clientId, onRefresh }) {
  return (
    <div className="space-y-5" data-testid="profile-tiers">
      <Tier
        numeral="I"
        title="The Essentials"
        lede="The few things every journey begins with — name, contact, how they like to hear from you."
        fields={TIER_I_FIELDS}
        client={client}
        clientId={clientId}
        onRefresh={onRefresh}
        defaultOpen
        testid="tier-essentials"
      />
      <Tier
        numeral="II"
        title="Travel Details"
        lede="Passport, dietary, loyalty — added as the relationship unfolds."
        fields={TIER_II_FIELDS}
        client={client}
        clientId={clientId}
        onRefresh={onRefresh}
        testid="tier-travel"
      />
      <Tier
        numeral="III"
        title="The Inner Circle"
        lede="VIP cues, celebrations, gifting preferences — the quiet long-form intelligence."
        fields={TIER_III_FIELDS}
        client={client}
        clientId={clientId}
        onRefresh={onRefresh}
        testid="tier-inner-circle"
      />
    </div>
  );
}

export default ProfileTiers;
